import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import type { DestinationContext } from "@/lib/dashboard-home/destinations";
import { resolveShortcuts } from "@/lib/dashboard-home/resolve-shortcuts";
import {
  MAX_SHORTCUTS,
  reorderShortcuts,
  type StoredShortcut,
} from "@/lib/dashboard-home/shortcuts";
import { GripVertical, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { HomeDialogHeader } from "../HomeDialogHeader";
import { ShortcutsIcon } from "../HomeIcons";
import { AddShortcutView } from "./add/AddShortcutView";
import { ShortcutCounter } from "./ShortcutCounter";
import { ShortcutsEditor } from "./ShortcutsEditor";

/** Durée de la mise en évidence du raccourci tout juste ajouté. */
const HIGHLIGHT_MS = 1800;

type ManageShortcutsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shortcuts: StoredShortcut[];
  context: DestinationContext;
  /** Chaque changement (ajout, suppression, ordre) est enregistré aussitôt. */
  onChange: (shortcuts: StoredShortcut[]) => void;
  onOpenPath: (path: string) => void;
};

/**
 * Modale des raccourcis : la grille (réordonner, supprimer) puis, sur un
 * second écran, l'ajout. Montée à chaque ouverture (`key` côté page) : elle
 * rouvre toujours sur la grille.
 */
export const ManageShortcutsDialog = ({
  open,
  onOpenChange,
  shortcuts,
  context,
  onChange,
  onOpenPath,
}: ManageShortcutsDialogProps) => {
  const vt = useVisibleT();
  const [adding, setAdding] = useState(false);
  const [highlightId, setHighlightId] = useState<string | null>(null);

  useEffect(() => {
    if (!highlightId) return;
    const timer = setTimeout(() => setHighlightId(null), HIGHLIGHT_MS);
    return () => clearTimeout(timer);
  }, [highlightId]);

  const items = resolveShortcuts(shortcuts, context, vt);
  const full = shortcuts.length >= MAX_SHORTCUTS;

  const add = (shortcut: StoredShortcut) => {
    if (full) return;
    onChange([...shortcuts, shortcut]);
    setHighlightId(shortcut.id);
    setAdding(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        needCloseButton={false}
        overlay="light"
        className="max-h-[90vh] max-w-3xl grid-cols-[minmax(0,1fr)] gap-0 overflow-y-auto overflow-x-hidden rounded-xl p-6 text-slate-900 sm:rounded-xl"
      >
        {adding ? (
          <AddShortcutView
            shortcuts={shortcuts}
            context={context}
            onAdd={add}
            onBack={() => setAdding(false)}
            onOpenPath={(path) => {
              onOpenChange(false);
              onOpenPath(path);
            }}
          />
        ) : (
          <>
            <HomeDialogHeader
              icon={<ShortcutsIcon />}
              iconBoxClassName="bg-slate-100"
              title={vt("bo.home.shortcuts.title", "Raccourcis")}
              description={vt(
                "bo.home.shortcuts.manageSubtitleAuto",
                "Glissez-déposez pour réorganiser, supprimez ou ajoutez des raccourcis. Tout est enregistré automatiquement.",
              )}
            />

            <ShortcutsEditor
              items={items}
              highlightId={highlightId}
              onReorder={(ids) => onChange(reorderShortcuts(shortcuts, ids))}
              onRemove={(id) => onChange(shortcuts.filter((s) => s.id !== id))}
            />

            <button
              type="button"
              onClick={() => setAdding(true)}
              disabled={full}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-full border border-dashed border-slate-400 px-4 py-2.5 text-sm transition-colors hover:border-primary hover:bg-brand-50 hover:text-primary disabled:pointer-events-none disabled:opacity-50"
            >
              <Plus className="size-4" aria-hidden />
              {full
                ? vt(
                    "bo.home.shortcuts.fullShort",
                    "Maximum atteint : supprimez un raccourci pour en ajouter",
                  )
                : vt("bo.home.shortcuts.addButton", "Ajouter un raccourci")}
            </button>

            <footer className="mt-5 flex flex-wrap items-center justify-between gap-3">
              <span className="flex items-center gap-2 text-xs text-slate-500">
                <GripVertical className="size-4" aria-hidden />
                {vt("bo.home.shortcuts.dragHint", "Maintenez pour déplacer")}
              </span>
              <div className="ml-auto flex items-center gap-3">
                <ShortcutCounter count={shortcuts.length} />
                <Button type="button" onClick={() => onOpenChange(false)}>
                  {vt("bo.home.shortcuts.done", "Terminé")}
                </Button>
              </div>
            </footer>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
